import React, { useCallback, KeyboardEvent } from 'react';
import { FloatingFocusManager, FloatingPortal, useTransitionStyles } from '@floating-ui/react';
import { useFocusRing } from '@react-aria/focus';
import classNames from 'classnames';
import { getKeyFromItem } from '../../utils/shared';
import { getZIndex } from '../../styles';
import { FLOATING_ROOT_ID } from '../../constants';
import { AriaFocusProps } from '../../types/common';
import {
  dropdown_filter_item_base_css,
  dropdown_item_group_focus_style,
  dropdown_filter_item_group_css,
  dropdown_item_group_css,
} from '../dropdown/styles';
import { semantic_colors } from '../../foundation';
import styled from '@emotion/styled';
import { Stack } from '../stack';
import { TextButton } from '../button';
import { Button } from '../button';
import { IconReset, IconSearch } from '../icons/generated';
import { Input } from '../input';
import { useProviderConfig } from '../provider';
import { Checkbox } from '../checkbox';
import { FormLabel } from '../form';
import { Radio } from '../radio';
import { InputSize } from '../input/types';
import { getInputFontStyle } from '../input/styles';
import { DropdownFilterGroupOption, DropdownFilterOption } from './types';
import { DropdownOptionsProps } from './DropdownOptions';

export type Kind = 'radio' | 'checkbox';

export interface DropdownFilterOptionsProps<OptionValue>
  extends Omit<DropdownOptionsProps<OptionValue>, 'isPositioned' | 'estimateSize' | 'value' | 'options'> {
  showFilter: boolean;
  isAllCheck: boolean;
  filterPlaceholder?: string;
  value?: OptionValue[];
  isOptionGroup?: boolean;
  options: Array<DropdownFilterGroupOption<OptionValue> | DropdownFilterOption<OptionValue>>;
  searchValue?: string;
  /** @default true */
  showConfirm?: boolean;
  onChangeSearchValue?: (value: string) => void;
  selectedList: OptionValue[];
  kind: Kind;
  onConfirm?: (value: OptionValue[]) => void;
  onAllCheck?: () => void;
  defaultValues?: OptionValue[];
}

const prefix_component = {
  radio: Radio,
  checkbox: Checkbox,
};

const getGroupOptionsFlatIndex = <OptionValue extends string | number>(
  options: DropdownFilterGroupOption<OptionValue>[],
  originIndex: number,
  key: OptionValue,
) => {
  const focusIndexGroup = options.findIndex((option) => option.key === key);
  return focusIndexGroup === 0 ? originIndex : originIndex + options[focusIndexGroup - 1].options.length;
};

export const DropdownFilterOptions = <OptionValue extends string | number>({
  className,
  zIndex = getZIndex('dropdown'),
  size: sizeProp = 'medium',
  options,
  onClose,
  noDataText,
  value: valueProp,
  unstableFocusVisible,
  keyExtractor = getKeyFromItem,
  onChange,
  focusIndex,
  context,
  getFloatingProps,
  getItemProps,
  floatingStyles,
  refs,
  setRefs,
  setFocusIndex,
  showFilter,
  isAllCheck,
  isOptionGroup,
  filterPlaceholder,
  searchValue,
  onChangeSearchValue,
  selectedList,
  kind,
  showConfirm = true,
  onConfirm,
  onAllCheck,
  defaultValues = [],
}: DropdownFilterOptionsProps<OptionValue> & AriaFocusProps) => {
  const { locale } = useProviderConfig();
  const { isFocusVisible, focusProps } = useFocusRing({ within: true });

  const itemGroupStyle = [
    dropdown_filter_item_group_css,
    {
      ...(sizeProp === 'large' && { gap: '16px' }),
    },
  ];
  const isLargeSize = sizeProp === 'large';

  const { isMounted, styles } = useTransitionStyles(context, {
    duration: 100,
    initial: {
      opacity: 0,
    },
    common: {
      transitionTimingFunction: 'linear',
    },
  });

  const handleChange = useCallback(
    (value: OptionValue) => {
      onChange?.(value);
      // @ts-expect-error : TS2345
      setFocusIndex?.(null);
    },
    [onChange],
  );

  const renderAllCheckOption = () => {
    return (
      <li
        role='option'
        id={`pds-dropdown-filter-option-all`}
        css={[
          dropdown_filter_item_base_css,
          { ...(focusIndex === 0 && isFocusVisible && { ...dropdown_item_group_focus_style }) },
        ]}
        tabIndex={focusIndex === 0 ? 0 : -1}
        ref={(node) => setRefs(node, 0)}
        {...getItemProps({
          onKeyDown(event: KeyboardEvent) {
            if (event.key === 'Enter') {
              event.preventDefault();
              onConfirm?.(selectedList);
              onClose();
            }
            if (event.key === ' ') {
              event.preventDefault();
              onAllCheck?.();
            }
          },
        })}
      >
        <FilterItemWrapper>
          <Checkbox
            unstableFocusVisible={false}
            indeterminate={selectedList.length > 0 && selectedList.length < options.length}
            checked={selectedList.length === options.length}
            size={isLargeSize ? 'large' : 'medium'}
            onChange={() => onAllCheck?.()}
          >
            {locale?.DropdownFilter.all}
          </Checkbox>
        </FilterItemWrapper>
      </li>
    );
  };

  const renderToCheckOptionList = (optionList: DropdownFilterOption<OptionValue>[], key?: OptionValue) => {
    return optionList.map((option, idx) => {
      const { value, label, disabled } = option;
      const selected = valueProp?.includes(option.value);
      const currentIndex = isAllCheck ? idx + 1 : idx;
      const index =
        isOptionGroup && key
          ? getGroupOptionsFlatIndex(options as DropdownFilterGroupOption<OptionValue>[], currentIndex, key)
          : currentIndex;

      const PrefixComponent = prefix_component[kind];
      return (
        <li
          role='option'
          id={`pds-dropdown-filter-option-${value}`}
          css={[
            dropdown_filter_item_base_css,
            { ...(focusIndex === index && isFocusVisible && { ...dropdown_item_group_focus_style }) },
          ]}
          key={keyExtractor({ disabled, value, label }, index)}
          tabIndex={focusIndex === index ? 0 : -1}
          aria-selected={selected && focusIndex === index}
          data-selected={selected}
          data-value={value}
          aria-disabled={disabled}
          ref={(node) => setRefs(node, index)}
          {...getItemProps({
            onKeyDown(event: KeyboardEvent) {
              if (event.key === 'Enter') {
                event.preventDefault();
                onConfirm?.(selectedList);
                onClose();
              }
              if (event.key === ' ') {
                event.preventDefault();
                handleChange(value);
              }
            },
          })}
        >
          <FilterItemWrapper>
            <PrefixComponent
              unstableFocusVisible={false}
              size={isLargeSize ? 'large' : 'medium'}
              checked={selectedList.includes(option.value)}
              disabled={disabled}
              onChange={() => handleChange(option.value)}
            >
              {label}
            </PrefixComponent>
          </FilterItemWrapper>
        </li>
      );
    });
  };
  const renderToNoData = () =>
    noDataText ? (
      <Stack direction='column' width='100%' p={isLargeSize ? 16 : 12}>
        <NoDataItem size={sizeProp}>{noDataText}</NoDataItem>
      </Stack>
    ) : (
      <></>
    );

  return (
    <>
      {isMounted && (
        <FloatingPortal id={FLOATING_ROOT_ID}>
          <FloatingFocusManager context={context} initialFocus={refs.floating} returnFocus={false}>
            <div
              {...getFloatingProps({
                ref: refs.setFloating,
                className: classNames('pds-dropdown-filter', className),
                style: {
                  ...floatingStyles,
                  zIndex,
                  outline: 'none',
                  ...styles,
                },
                onKeyDown(event: KeyboardEvent) {
                  if (event.key === 'Tab') {
                    onClose();
                  }
                },
              })}
              css={[
                dropdown_item_group_css,
                { padding: 0 },
                { ...((unstableFocusVisible ?? isFocusVisible) && dropdown_item_group_focus_style) },
              ]}
              {...focusProps}
            >
              {showFilter && (
                <SearchArea direction='row' p={12}>
                  <Input
                    tabIndex={-1}
                    value={searchValue}
                    placeholder={filterPlaceholder ?? locale?.Dropdown.filterPlaceholder}
                    size={isLargeSize ? 'medium' : 'xsmall'}
                    leftIcon={<IconSearch color={semantic_colors.content.tertiary} />}
                    onChange={(e) => onChangeSearchValue?.(e.target.value)}
                  />
                </SearchArea>
              )}
              {isOptionGroup ? (
                options.length > 0 ? (
                  <SafeArea direction='column' gap={isLargeSize ? 20 : 12} p={isLargeSize ? 16 : 12}>
                    {(options as DropdownFilterGroupOption<OptionValue>[]).map(({ key, label, options }) => {
                      return (
                        <Stack direction='column' key={key} width='100%' gap={isLargeSize ? 12 : 8}>
                          <FormLabel>{label}</FormLabel>
                          <ul css={itemGroupStyle}>{renderToCheckOptionList(options, key)}</ul>
                        </Stack>
                      );
                    })}
                  </SafeArea>
                ) : (
                  renderToNoData()
                )
              ) : options.length > 0 ? (
                <SafeArea direction='column' width='100%' p={isLargeSize ? 16 : 12}>
                  <ul css={itemGroupStyle}>
                    {!showFilter && isAllCheck && kind === 'checkbox' && renderAllCheckOption()}
                    {renderToCheckOptionList(options as DropdownFilterOption<OptionValue>[])}
                  </ul>
                </SafeArea>
              ) : (
                renderToNoData()
              )}
              {showConfirm && (
                <ConfirmArea direction='row' p={12}>
                  <TextButton
                    variant='link'
                    size='medium'
                    leftIcon={<IconReset />}
                    color={semantic_colors.content.secondary}
                    onClick={() => onConfirm?.(defaultValues)}
                  >
                    {locale?.DropdownFilter.reset}
                  </TextButton>
                  <Button
                    variant='filled'
                    color='primary'
                    onClick={() => {
                      onConfirm?.(selectedList);
                      onClose();
                    }}
                    style={{ marginLeft: 'auto' }}
                  >
                    {locale?.DropdownFilter.confirm}
                  </Button>
                </ConfirmArea>
              )}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};

const SafeArea = styled(Stack)`
  width: 100%;
  overflow: auto;
`;

const SearchArea = styled(Stack)`
  width: 100%;
  border-bottom: 1px solid ${semantic_colors.border.primary};
`;

const ConfirmArea = styled(Stack)`
  width: 100%;
  border-top: 1px solid ${semantic_colors.border.primary};
`;

const NoDataItem = styled.li<{ size: InputSize }>`
  color: ${semantic_colors.content.tertiary};
  background-color: ${semantic_colors.background.surface};
  list-style: none;
  ${({ size }) => getInputFontStyle(size)};
`;

const FilterItemWrapper = styled.div`
  width: 100%;
  display: flex;
  label {
    width: 100%;
  }
`;
